import React, { useState, useEffect } from 'react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ChefDashboard = ({ user }) => {
  const [grantApplications, setGrantApplications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    try {
      // Fetch grant applications
      const grantResponse = await axios.get(`${API}/grants/my-applications`);
      setGrantApplications(grantResponse.data || []);
    } catch (error) {
      console.error('Failed to fetch dashboard data:', error);
    }
    setLoading(false);
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      pending: { bg: 'bg-yellow-100', text: 'text-yellow-800', label: '⏳ Pending Review' },
      under_review: { bg: 'bg-blue-100', text: 'text-blue-800', label: '🔍 Under Review' },
      approved: { bg: 'bg-green-100', text: 'text-green-800', label: '✅ Approved' },
      funded: { bg: 'bg-purple-100', text: 'text-purple-800', label: '💰 Funded' },
      rejected: { bg: 'bg-red-100', text: 'text-red-800', label: '❌ Rejected' }
    };
    return statusConfig[status] || statusConfig.pending;
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', { 
      year: 'numeric', 
      month: 'long', 
      day: 'numeric' 
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading your dashboard...</p>
        </div>
      </div>
    );
  }

  const latestApplication = grantApplications[0];
  const hasApplication = grantApplications.length > 0;

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-7xl mx-auto px-4">
        {/* Header */}
        <div className="mb-8 flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 mb-2">
              👨‍🍳 Chef Dashboard
            </h1>
            <p className="text-gray-600">
              Welcome back, {user?.full_name || 'Chef'}! Track your grant application and compliance status.
            </p>
          </div>
          <a
            href="/create-post"
            className="flex items-center space-x-2 px-6 py-3 bg-purple-600 hover:bg-purple-700 text-white rounded-lg font-medium transition shadow-lg"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
            </svg>
            <span>Create Post</span>
          </a>
        </div>

        {/* Tabs */}
        <div className="mb-6 border-b border-gray-200">
          <nav className="flex space-x-8">
            <button
              onClick={() => setActiveTab('overview')}
              className={`py-4 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'overview'
                  ? 'border-purple-600 text-purple-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              📊 Overview
            </button>
            <button
              onClick={() => setActiveTab('grant')}
              className={`py-4 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'grant'
                  ? 'border-purple-600 text-purple-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              🌱 Grant Application
            </button>
            <button
              onClick={() => setActiveTab('compliance')}
              className={`py-4 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'compliance'
                  ? 'border-purple-600 text-purple-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              📋 Compliance
            </button>
          </nav>
        </div>

        {/* Overview Tab */}
        {activeTab === 'overview' && (
          <div className="space-y-6">
            {/* Grant Status Card */}
            {hasApplication ? (
              <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-200">
                <div className="flex items-start justify-between mb-4">
                  <div>
                    <h2 className="text-xl font-bold text-gray-900 mb-1">
                      🌱 Lambalia Cottage Permit Grant
                    </h2>
                    <p className="text-sm text-gray-600">
                      Application submitted on {formatDate(latestApplication.application_date)}
                    </p>
                  </div>
                  <span className={`px-4 py-2 rounded-full text-sm font-medium ${getStatusBadge(latestApplication.status).bg} ${getStatusBadge(latestApplication.status).text}`}>
                    {getStatusBadge(latestApplication.status).label}
                  </span>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                  <div className="bg-gray-50 p-4 rounded-lg">
                    <p className="text-sm text-gray-600 mb-1">Estimated Cost</p>
                    <p className="text-2xl font-bold text-gray-900">
                      ${latestApplication.estimated_cost}
                    </p>
                  </div>
                  <div className="bg-gray-50 p-4 rounded-lg">
                    <p className="text-sm text-gray-600 mb-1">State</p>
                    <p className="text-lg font-semibold text-gray-900">
                      {latestApplication.state_name || latestApplication.state_code}
                    </p>
                  </div>
                  <div className="bg-gray-50 p-4 rounded-lg">
                    <p className="text-sm text-gray-600 mb-1">Cuisine Type</p>
                    <p className="text-lg font-semibold text-gray-900">
                      {latestApplication.cuisine_type}
                    </p>
                  </div>
                </div>

                {/* Timeline */}
                <div className="border-t pt-4">
                  <h3 className="font-semibold text-gray-900 mb-3">Application Timeline</h3>
                  <div className="space-y-3">
                    <div className="flex items-start">
                      <div className="flex-shrink-0 w-8 h-8 bg-green-100 rounded-full flex items-center justify-center mr-3">
                        <span className="text-green-600">✓</span>
                      </div>
                      <div>
                        <p className="font-medium text-gray-900">Application Submitted</p>
                        <p className="text-sm text-gray-600">{formatDate(latestApplication.application_date)}</p>
                      </div>
                    </div>

                    {latestApplication.status === 'pending' && (
                      <div className="flex items-start">
                        <div className="flex-shrink-0 w-8 h-8 bg-yellow-100 rounded-full flex items-center justify-center mr-3">
                          <span className="text-yellow-600">⏳</span>
                        </div>
                        <div>
                          <p className="font-medium text-gray-900">Pending Review</p>
                          <p className="text-sm text-gray-600">We'll review your application within 5-7 business days</p>
                        </div>
                      </div>
                    )}

                    {latestApplication.status === 'approved' && (
                      <div className="flex items-start">
                        <div className="flex-shrink-0 w-8 h-8 bg-green-100 rounded-full flex items-center justify-center mr-3">
                          <span className="text-green-600">✓</span>
                        </div>
                        <div>
                          <p className="font-medium text-gray-900">Application Approved</p>
                          <p className="text-sm text-gray-600">Your grant has been approved! Awaiting fund disbursement.</p>
                        </div>
                      </div>
                    )}

                    {latestApplication.status === 'funded' && (
                      <div className="flex items-start">
                        <div className="flex-shrink-0 w-8 h-8 bg-purple-100 rounded-full flex items-center justify-center mr-3">
                          <span className="text-purple-600">💰</span>
                        </div>
                        <div>
                          <p className="font-medium text-gray-900">Grant Funded</p>
                          <p className="text-sm text-gray-600">Congratulations! Your grant has been disbursed.</p>
                        </div>
                      </div>
                    )}

                    {latestApplication.status === 'rejected' && (
                      <div className="flex items-start">
                        <div className="flex-shrink-0 w-8 h-8 bg-red-100 rounded-full flex items-center justify-center mr-3">
                          <span className="text-red-600">✗</span>
                        </div>
                        <div>
                          <p className="font-medium text-gray-900">Application Rejected</p>
                          <p className="text-sm text-gray-600">Unfortunately, your application was not approved. You may apply again in 30 days.</p>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ) : (
              <div className="bg-gradient-to-r from-purple-50 to-blue-50 rounded-xl shadow-sm p-8 border border-purple-200">
                <div className="text-center">
                  <div className="text-6xl mb-4">🌱</div>
                  <h2 className="text-2xl font-bold text-gray-900 mb-2">
                    Apply for Lambalia Cottage Permit Grant
                  </h2>
                  <p className="text-gray-600 mb-6 max-w-2xl mx-auto">
                    Get financial assistance to cover your cottage food permit fees. We help aspiring home chefs start their culinary journey.
                  </p>
                  <a
                    href="/grant-application"
                    className="inline-block bg-purple-600 hover:bg-purple-700 text-white font-medium py-3 px-8 rounded-lg transition"
                  >
                    Apply for Grant →
                  </a>
                </div>
              </div>
            )}

            {/* Quick Actions */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="bg-white rounded-lg shadow-sm p-6 border border-gray-200">
                <div className="text-3xl mb-3">📋</div>
                <h3 className="font-semibold text-gray-900 mb-2">Compliance Status</h3>
                <p className="text-sm text-gray-600 mb-4">
                  View your state requirements and upload documents
                </p>
                <button
                  onClick={() => setActiveTab('compliance')}
                  className="text-purple-600 hover:text-purple-700 font-medium text-sm"
                >
                  View Compliance →
                </button>
              </div>

              <div className="bg-white rounded-lg shadow-sm p-6 border border-gray-200">
                <div className="text-3xl mb-3">📄</div>
                <h3 className="font-semibold text-gray-900 mb-2">Resources</h3>
                <p className="text-sm text-gray-600 mb-4">
                  Access guides and state-specific information
                </p>
                <a
                  href="#resources"
                  className="text-purple-600 hover:text-purple-700 font-medium text-sm"
                >
                  View Resources →
                </a>
              </div>

              <div className="bg-white rounded-lg shadow-sm p-6 border border-gray-200">
                <div className="text-3xl mb-3">📞</div>
                <h3 className="font-semibold text-gray-900 mb-2">Need Help?</h3>
                <p className="text-sm text-gray-600 mb-4">
                  Contact our support team for assistance
                </p>
                <a
                  href="mailto:helpdesk@lambalia.com"
                  className="text-purple-600 hover:text-purple-700 font-medium text-sm"
                >
                  Contact Support →
                </a>
              </div>
            </div>
          </div>
        )}

        {/* Grant Tab */}
        {activeTab === 'grant' && (
          <div className="space-y-6">
            {hasApplication ? (
              <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-200">
                <h2 className="text-2xl font-bold text-gray-900 mb-6">Grant Application Details</h2>
                
                <div className="space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="text-sm font-medium text-gray-600">Full Name</label>
                      <p className="text-gray-900">{latestApplication.full_name}</p>
                    </div>
                    <div>
                      <label className="text-sm font-medium text-gray-600">Email</label>
                      <p className="text-gray-900">{latestApplication.email}</p>
                    </div>
                    <div>
                      <label className="text-sm font-medium text-gray-600">Phone</label>
                      <p className="text-gray-900">{latestApplication.phone}</p>
                    </div>
                    <div>
                      <label className="text-sm font-medium text-gray-600">State</label>
                      <p className="text-gray-900">{latestApplication.state_name}</p>
                    </div>
                    <div>
                      <label className="text-sm font-medium text-gray-600">City</label>
                      <p className="text-gray-900">{latestApplication.city}</p>
                    </div>
                    <div>
                      <label className="text-sm font-medium text-gray-600">Zip Code</label>
                      <p className="text-gray-900">{latestApplication.zip_code}</p>
                    </div>
                  </div>

                  <div>
                    <label className="text-sm font-medium text-gray-600">Why do you want to become a chef?</label>
                    <p className="text-gray-900 mt-1">{latestApplication.why_chef}</p>
                  </div>

                  <div>
                    <label className="text-sm font-medium text-gray-600">Cuisine Type</label>
                    <p className="text-gray-900">{latestApplication.cuisine_type}</p>
                  </div>

                  {latestApplication.business_plan && (
                    <div>
                      <label className="text-sm font-medium text-gray-600">Business Plan</label>
                      <p className="text-gray-900 mt-1">{latestApplication.business_plan}</p>
                    </div>
                  )}
                </div>
              </div>
            ) : (
              <div className="bg-white rounded-xl shadow-sm p-8 border border-gray-200 text-center">
                <div className="text-6xl mb-4">📝</div>
                <h2 className="text-2xl font-bold text-gray-900 mb-2">No Grant Application Yet</h2>
                <p className="text-gray-600 mb-6">
                  Start your journey by applying for the Lambalia Cottage Permit Grant
                </p>
                <a
                  href="/grant-application"
                  className="inline-block bg-purple-600 hover:bg-purple-700 text-white font-medium py-3 px-8 rounded-lg transition"
                >
                  Apply Now →
                </a>
              </div>
            )}
          </div>
        )}

        {/* Compliance Tab */}
        {activeTab === 'compliance' && (
          <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-200">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">Compliance Requirements</h2>
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-6">
              <p className="text-blue-800">
                📋 Once your grant is approved, you'll need to complete state-specific compliance requirements including obtaining your cottage food permit, food handler certification, and other necessary documents.
              </p>
            </div>
            
            {user?.state_code && (
              <div className="mt-6">
                <h3 className="font-semibold text-gray-900 mb-3">
                  Requirements for {user.state_code}
                </h3>
                <ul className="space-y-2">
                  <li className="flex items-start">
                    <span className="text-gray-400 mr-2">•</span>
                    <span className="text-gray-700">Cottage Food Permit</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-gray-400 mr-2">•</span>
                    <span className="text-gray-700">Food Handler Certification</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-gray-400 mr-2">•</span>
                    <span className="text-gray-700">Liability Insurance (Recommended)</span>
                  </li>
                </ul>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default ChefDashboard;
